import React, { ReactNode, MouseEvent } from 'react';
import { Close } from '@mui/icons-material';
import {
  Stack,
  Button,
  Breakpoint,
  Typography,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent,
  Dialog as MuiDialog,
  DialogProps as MuiDialogProps,
  CircularProgress,
} from '@mui/material';
import { useIsMobile } from 'hooks';

type DialogProps = Pick<MuiDialogProps, 'disableRestoreFocus' | 'open' | 'disableEnforceFocus'> & {
  title: ReactNode;
  size?: Breakpoint;
  content: ReactNode;
  hasDivider?: boolean;
  subAction?: ReactNode;
  showActions?: boolean;
  onClose?: VoidFunction;
  primaryActionTitle?: string;
  secondaryActionTitle?: string;
  isSecondaryActionDisabled?: boolean;
  isPrimaryActionDisabled?: boolean;
  isPrimaryActionLoading?: boolean;
  onPrimaryActionClick?: (event: React.BaseSyntheticEvent) => void;
  onSecondaryActionClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  tertiaryAction?: {
    tertiaryActionTitle?: string;
    onTertiaryActionClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  };
  sx?: MuiDialogProps['sx'];
};

export const Dialog = ({
  open,
  title,
  content,
  onClose,
  subAction,
  size = 'xs',
  showActions = true,
  hasDivider = false,
  primaryActionTitle,
  onPrimaryActionClick,
  onSecondaryActionClick,
  isSecondaryActionDisabled,
  isPrimaryActionDisabled,
  isPrimaryActionLoading,
  secondaryActionTitle = 'abbrechen',
  tertiaryAction: { tertiaryActionTitle, onTertiaryActionClick } = {},
  sx,
  ...props
}: DialogProps) => {
  const closeDialog = onClose ? onClose : onSecondaryActionClick;
  const { isMobile } = useIsMobile();

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !isPrimaryActionDisabled && !isPrimaryActionLoading) {
      onPrimaryActionClick?.(event);
    }
  };

  return (
    <MuiDialog
      {...props}
      open={open}
      fullWidth
      maxWidth={size}
      onClose={closeDialog}
      onKeyDown={handleKeyDown}
      sx={{
        ...sx,
        '.MuiPaper-root': (theme) => ({
          margin: 4,
          width: `calc(100% - ${theme.spacing(8)})`,
        }),
      }}
    >
      <DialogTitle sx={{ pb: hasDivider ? 5 : 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="start">
          <Typography variant="subtitle1">{title}</Typography>
          <IconButton onClick={closeDialog} sx={{ p: 1, color: 'grey.800' }}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers={hasDivider}>{content}</DialogContent>
      {showActions && (
        <DialogActions sx={{ pt: 0, display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
          {tertiaryActionTitle && (
            <Button variant="text" onClick={onTertiaryActionClick} fullWidth={isMobile}>
              {tertiaryActionTitle}
            </Button>
          )}
          <Button
            variant="outlined"
            fullWidth={isMobile}
            onClick={onSecondaryActionClick}
            disabled={isSecondaryActionDisabled}
          >
            {secondaryActionTitle}
          </Button>
          {subAction ? subAction : null}
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={onPrimaryActionClick}
            disabled={isPrimaryActionDisabled || isPrimaryActionLoading}
          >
            {isPrimaryActionLoading ? <CircularProgress color="inherit" size={20} /> : primaryActionTitle}
          </Button>
        </DialogActions>
      )}
    </MuiDialog>
  );
};
